import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import moment from "moment";
import { useHistory } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import PictureBanner from './PictureBanner';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E7F0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    marginBottom: '12px',
    display: 'grid',
    gridTemplateColumns: '120px minmax(0, 1fr)',
    cursor: 'pointer',
    minHeight: '136px',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '120px minmax(0, 1fr)',
      minHeight: '136px',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '120px minmax(0, 1fr)',
      minHeight: '136px',
    },
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
  },
  details: {
    padding: '10px 12px',
    textAlign: 'left',
    minWidth: 0,
  },
  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#1A1A1A',
    lineHeight: 1.25,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.95rem',
    },
  },
  time: {
    color: '#2A2A2A',
    fontSize: '0.8rem',
    marginBottom: '8px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
      marginBottom: '8px',
    },
  },
  status: {
    fontSize: '0.9rem',
    fontWeight: 600,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
    },
  },
  numbers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    maxHeight: '68px',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      gap: '6px',
      maxHeight: '68px',
    },
  },
  numberBadge: {
    color: '#243241',
    fontWeight: 500,
    fontSize: '0.7rem',
    backgroundColor: '#8EEA8E',
    borderRadius: '7px',
    padding: '4px 8px',
    lineHeight: 1.1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      padding: '4px 8px',
      borderRadius: '7px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem',
      padding: '4px 8px',
      borderRadius: '7px',
    },
  },
  moreBadge: {
    color: '#1A1A1A',
    fontWeight: 500,
    fontSize: '0.8rem',
    padding: '0 2px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
    },
  },
}));

function getStatusColor(status) {
  if (['paid', 'completed', 'paid_after_time_limit'].includes(status)) return '#2E7D32';
  if (['created', 'waiting_payment'].includes(status)) return '#D97706';
  if (['error', 'expired', 'canceled'].includes(status)) return '#C62828';
  return '#2E7D32';
}

export default function Order({order, currentUser}) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  moment.locale('pt-BR');
  const statusLabel = order?.user_status === 'Compra aprovada' ? 'Aprovado' : order?.user_status;
  const visibleNumbers = 5;

  function handleClick() {
    history.push(`/${order.campaign?.influencer_username}/site/compras/${order.id}`);
  }

  return (
    <div className={classes.root} onClick={handleClick}>
      <div className={classes.imageContainer}>
        <PictureBanner
          avifUrl={order.campaign?.square_banner_url}
          webpUrl={order.campaign?.square_banner_webp_url}
          fallbackUrl={order.campaign?.square_banner_url}
        />
      </div>
      <div className={classes.details}>
        <div className={classes.topRow}>
          <Typography className={classes.title} component="h5" variant="h5">
            {order.campaign?.title}
          </Typography>
          <Typography className={classes.status} style={{ color: getStatusColor(order.status) }} variant="body1">
            {statusLabel}
          </Typography>
        </div>
        <Typography className={classes.time} variant="body1">
          {moment(order.created_at).format("DD/MM/YYYY [às] HH:mm")}
        </Typography>
        <Typography className={classes.numbers} variant="body1">
          {
            order?.status != 'canceled' &&
              order?.numbers?.slice(0, visibleNumbers).map((number, index) => (
                <span key={index} className={classes.numberBadge}>
                  {number.toString().padStart(order.campaign.digits, '0')}
                </span>
              ))
          }
          {order?.numbers_count > visibleNumbers && <span className={classes.moreBadge}>+{order.numbers_count - visibleNumbers}</span>}
        </Typography>
      </div>
    </div>
  );
}
